import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import CourseLeaderboard from "@/components/CourseLeaderboard";
import { ArrowLeft, Plus, Sparkles, Trash2, Share2, Copy, Rocket, Move } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WORLD_W = 1280;
const WORLD_H = 820;

// Editor map dims — keep aspect ratio of the world; clamp to a comfortable
// width so it fits inside the layout column.
const MAP_W = 720;
const MAP_H = Math.round(MAP_W * (WORLD_H / WORLD_W));

export default function CourseEditor() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [newItemName, setNewItemName] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [dragging, setDragging] = useState(null); // { item_id, offsetX, offsetY }
  const mapRef = useRef(null);

  const load = async () => {
    try {
      const r = await axios.get(
        `${BACKEND_URL}/api/courses/${courseId}`,
        { withCredentials: true },
      );
      setCourse(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Course not found");
      navigate("/courses");
    }
  };

  useEffect(() => {
    load();
  }, [courseId]); // eslint-disable-line

  const addItem = async () => {
    const label = newItemName.trim();
    if (!label) return;
    if ((course?.items?.length || 0) >= 5) {
      toast.error("Max 5 items per course");
      return;
    }
    if (course?.is_published) {
      toast.error("Course is published — cannot edit");
      return;
    }
    setGenBusy(true);
    toast.info("Generating icon + trivia… (~10-20s)", { id: "gen-toast" });
    try {
      await axios.post(
        `${BACKEND_URL}/api/courses/${courseId}/items`,
        { name: label },
        { withCredentials: true },
      );
      toast.success(`'${label}' added — AI icon + trivia ready`, { id: "gen-toast" });
      setNewItemName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate item", { id: "gen-toast" });
    } finally {
      setGenBusy(false);
    }
  };

  const removeItem = async (item_id) => {
    if (!window.confirm("Remove this item?")) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/courses/${courseId}/items/${item_id}/delete`,
        {},
        { withCredentials: true },
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const publish = async () => {
    if ((course?.items?.length || 0) < 3) {
      toast.error("Need at least 3 items to publish");
      return;
    }
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/courses/${courseId}/publish`,
        {},
        { withCredentials: true },
      );
      toast.success("🚀 Published! Every racer will see the alert.", { duration: 6000 });
      setCourse(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  const copyShare = () => {
    if (!course?.share_code) return;
    const url = `${window.location.origin}/courses/play/${course.share_code}`;
    navigator.clipboard?.writeText(url);
    toast.success("Share link copied");
  };

  // ─── Item drag-to-position on the editor map ────────────────────────────
  const onItemMouseDown = (e, item) => {
    if (course?.is_published) return;
    e.preventDefault();
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setDragging({
      item_id: item.item_id,
      // Track the offset from the icon's center so the icon doesn't snap
      // to the cursor's top-left when you start dragging.
      offsetX: 0,
      offsetY: 0,
    });
  };

  const onMapMouseMove = (e) => {
    if (!dragging) return;
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    const wx = Math.round((px / rect.width) * WORLD_W);
    const wy = Math.round((py / rect.height) * WORLD_H);
    // Optimistic update so the icon follows the cursor smoothly
    setCourse((cur) =>
      cur
        ? {
            ...cur,
            items: cur.items.map((it) =>
              it.item_id === dragging.item_id ? { ...it, x: wx, y: wy } : it,
            ),
          }
        : cur,
    );
  };

  const onMapMouseUp = async () => {
    if (!dragging) return;
    const target = course?.items?.find((it) => it.item_id === dragging.item_id);
    setDragging(null);
    if (!target) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/courses/${courseId}/items/${target.item_id}/position`,
        { x: target.x, y: target.y },
        { withCredentials: true },
      );
    } catch (_e) {
      // non-fatal — refresh from server to undo any clamp mismatch
      load();
    }
  };

  if (!course) {
    return (
      <AppLayout>
        <div className="px-6 py-10 text-white/60 text-sm">Loading course…</div>
      </AppLayout>
    );
  }

  const publishedReady = (course.items?.length || 0) >= 3;
  const itemCount = course.items?.length || 0;

  return (
    <AppLayout>
      <div
        className="px-6 lg:px-12 py-10 max-w-[1280px] mx-auto"
        data-testid="course-editor-page"
        onMouseMove={onMapMouseMove}
        onMouseUp={onMapMouseUp}
      >
        <button
          onClick={() => navigate("/courses")}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4"
          data-testid="course-editor-back"
        >
          <ArrowLeft className="w-4 h-4" /> All courses
        </button>
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="label-kicker">
              {course.is_published ? "Live · Published" : "Editor · Draft"}
            </div>
            <h1 className="font-heading text-5xl">{course.name}</h1>
            <div className="text-white/60 mt-1 text-sm">
              Base: <b className="text-white">{course.base_city_name}</b> ·{" "}
              {itemCount}/5 items
            </div>
            {course.description && (
              <div className="text-white/50 text-sm mt-2 max-w-2xl">{course.description}</div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {course.is_published ? (
              <>
                <div
                  className="border border-[#34C759]/40 bg-[#34C759]/10 px-3 py-2 text-[#34C759] font-mono text-xs tracking-widest"
                  data-testid="course-editor-share-code"
                >
                  CODE · {course.share_code}
                </div>
                <button
                  onClick={copyShare}
                  className="btn-ghost text-xs flex items-center gap-2"
                  data-testid="course-editor-copy-link"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
              </>
            ) : (
              <button
                disabled={!publishedReady}
                onClick={publish}
                className={`btn-primary flex items-center gap-2 ${
                  !publishedReady ? "opacity-40 cursor-not-allowed" : ""
                }`}
                data-testid="course-editor-publish"
              >
                <Rocket className="w-4 h-4" />
                {publishedReady ? "Publish & alert all racers" : `Add ${3 - itemCount} more to publish`}
              </button>
            )}
          </div>
        </div>

        {/* Editor body: items list + draggable map */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT — items + add form */}
          <div className="lg:col-span-5 space-y-3">
            {!course.is_published && (
              <div className="surface-solid p-5" data-testid="course-editor-add">
                <div className="label-kicker">Generate new artifact</div>
                <div className="font-heading text-xl mt-1 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#FFCC00]" />
                  Describe an item
                </div>
                <div className="flex gap-2">
                  <input
                    data-testid="course-editor-newitem-name"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder='e.g. "fedora", "boombox", "vinyl record"'
                    disabled={genBusy || itemCount >= 5}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addItem();
                    }}
                    className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm focus:border-[#FFCC00] outline-none disabled:opacity-50"
                  />
                  <button
                    data-testid="course-editor-newitem-add"
                    onClick={addItem}
                    disabled={genBusy || itemCount >= 5 || !newItemName.trim()}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {genBusy ? (
                      <>
                        <Sparkles className="w-4 h-4 animate-spin" /> AI…
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" /> Add
                      </>
                    )}
                  </button>
                </div>
                <div className="text-[11px] text-white/40 mt-2">
                  Nano Banana draws the icon. Claude Sonnet 4.5 writes the trivia + 4 answers. ~10-20s per item.
                </div>
              </div>
            )}

            <div className="space-y-2" data-testid="course-editor-items">
              {course.items.map((it, idx) => (
                <div
                  key={it.item_id}
                  className="surface-solid p-4 flex gap-3 items-start"
                  data-testid={`course-editor-item-${it.item_id}`}
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-black border border-white/10 shrink-0">
                    {it.icon_url ? (
                      <img
                        src={`${BACKEND_URL}${it.icon_url}`}
                        alt={it.name}
                        className="w-full h-full object-contain"
                        style={{ imageRendering: "pixelated" }}
                      />
                    ) : (
                      <Sparkles className="w-6 h-6 text-[#FFCC00]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-base">
                        {idx + 1}. {it.name}
                      </span>
                      <span className="text-[9px] tracking-widest text-white/30 uppercase font-mono">
                        ({it.x},{it.y})
                      </span>
                    </div>
                    <div className="text-xs text-white/70 mt-1 line-clamp-2">
                      Q: {it.quiz?.question}
                    </div>
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {(it.quiz?.options || []).map((opt, oi) => (
                        <div
                          key={oi}
                          className={`text-[11px] px-2 py-1 border ${
                            oi === it.quiz?.correct_index
                              ? "border-[#34C759]/50 bg-[#34C759]/10 text-[#34C759]"
                              : "border-white/10 text-white/50"
                          }`}
                        >
                          {String.fromCharCode(65 + oi)}. {opt}
                        </div>
                      ))}
                    </div>
                  </div>
                  {!course.is_published && (
                    <button
                      data-testid={`course-editor-item-${it.item_id}-delete`}
                      className="text-white/40 hover:text-[#FF3B30] p-1"
                      onClick={() => removeItem(it.item_id)}
                      title="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {itemCount === 0 && (
                <div className="surface-solid p-8 text-center text-white/50 text-sm" data-testid="course-editor-items-empty">
                  No items yet. Describe one above to generate it.
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — drag-to-position map preview */}
          <div className="lg:col-span-7">
            <div className="surface-solid p-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="label-kicker">Layout preview</div>
                  <div className="font-heading text-xl mt-1">
                    Drag items to place them
                  </div>
                </div>
                <div className="flex items-center gap-1 text-[10px] tracking-widest text-white/40 uppercase">
                  <Move className="w-3 h-3" /> Drag &amp; drop
                </div>
              </div>
              <div
                ref={mapRef}
                data-testid="course-editor-map"
                className="relative bg-[#1B2A1B] border border-white/10 select-none mx-auto"
                style={{
                  width: MAP_W,
                  height: MAP_H,
                  maxWidth: "100%",
                  backgroundImage:
                    "linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)",
                  backgroundSize: `${MAP_W / 8}px ${MAP_H / 5}px`,
                }}
              >
                {/* Road cross — anchor reference */}
                <div className="absolute left-1/2 top-0 bottom-0 w-[36px] -translate-x-1/2 bg-[#3a3a3a]" />
                <div className="absolute top-1/2 left-0 right-0 h-[36px] -translate-y-1/2 bg-[#3a3a3a]" />

                {course.items.map((it) => {
                  const px = (it.x / WORLD_W) * MAP_W;
                  const py = (it.y / WORLD_H) * MAP_H;
                  return (
                    <div
                      key={it.item_id}
                      data-testid={`course-editor-map-item-${it.item_id}`}
                      onMouseDown={(e) => onItemMouseDown(e, it)}
                      className={`absolute -translate-x-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-black border-2 border-[#FFCC00] cursor-grab active:cursor-grabbing ${
                        dragging?.item_id === it.item_id ? "ring-2 ring-[#FF3B30]" : ""
                      }`}
                      style={{ left: px, top: py }}
                      title={`${it.name} (${it.x}, ${it.y})`}
                    >
                      {it.icon_url ? (
                        <img
                          src={`${BACKEND_URL}${it.icon_url}`}
                          alt={it.name}
                          className="w-full h-full object-contain"
                          style={{ imageRendering: "pixelated", pointerEvents: "none" }}
                        />
                      ) : (
                        <Sparkles className="w-5 h-5 text-[#FFCC00]" />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-white/40 mt-3 tracking-widest uppercase">
                Road cross marked in grey · World {WORLD_W}×{WORLD_H}
              </div>
              {course.is_published && (
                <div className="mt-4 border border-[#34C759]/30 bg-[#34C759]/5 p-3 flex items-center gap-3" data-testid="course-editor-published-card">
                  <Share2 className="w-5 h-5 text-[#34C759]" />
                  <div className="flex-1">
                    <div className="text-sm font-heading text-[#34C759]">Live and shareable</div>
                    <div className="text-xs text-white/60">
                      Anyone with the link can run this heist —{" "}
                      <a
                        href={`/courses/play/${course.share_code}`}
                        className="text-[#FFCC00] hover:underline"
                      >
                        /courses/play/{course.share_code}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Leaderboard for the owner's course — shows even when empty. */}
            <div className="surface-solid p-5 mt-4">
              <CourseLeaderboard courseId={course.course_id} />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
